import React, { useState, useContext } from 'react'
import { Container } from './Container'
import { useNavigate } from 'react-router-dom'
import { ChatSupportContext } from '../../contexts/ChatSupportContext'

const faqs = [
  {
    question: 'How does AUDIT-AI integrate with my existing systems?',
    answer:
      'AUDIT-AI offers seamless integration with all major ERP systems via SFTP and automated jobs with a plan to implement direct API integrations. Our platform includes pre-built templates for SAP, Oracle, Microsoft Dynamics, NetSuite, and other popular systems. For custom or legacy systems, our implementation team can build tailored integrations to ensure a complete end-to-end solution.',
  },
  {
    question: 'How does the AI-powered process narrative generator work?',
    answer:
      'Our AI process narrative generator transforms system owner inputs into comprehensive, audit-ready documentation. Simply provide basic information about your process, and the AI analyzes it to identify control points, risks, and key stakeholders. It then generates a structured narrative that meets audit standards, complete with flowcharts and control matrices—reducing documentation time from weeks to hours.',
  },
  {
    question: 'Can AUDIT-AI replace my current audit management system?',
    answer:
      'Yes! AUDIT-AI is designed as a complete audit management solution with enhanced AI capabilities. It handles the entire audit lifecycle from planning to reporting, while adding intelligent automation for testing, documentation, and compliance monitoring. Most clients fully transition from their legacy systems within 1-2 months, with immediate productivity gains.',
  },
  {
    question: 'How does the automated user access review process work?',
    answer:
      'Our platform connects to your systems via SFTP and automated jobs, extracts current access data, and intelligently organizes it for review. It identifies appropriate reviewers, distributes personalized review tasks, flags high-risk access patterns, tracks responses, escalates when needed, and documents the entire process with a complete audit trail. This reduces review time by up to 80% while improving accuracy and control.',
  },
  
]

export function Faqs() {
  const [activeIndex, setActiveIndex] = useState(null);
  const navigate = useNavigate();
  const { chatSupportRef } = useContext(ChatSupportContext);
  
  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    if (chatSupportRef && chatSupportRef.current) {
      chatSupportRef.current.openChat();
    }
  };

  return (
    <section
      id="faqs"
      aria-labelledby="faqs-title"
      className="py-20 sm:py-32 relative overflow-hidden bg-gradient-to-b from-white to-gray-50"
    >
      {/* Professional premium background elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50 opacity-95"></div>
        
        {/* Professional abstract shapes */}
        <div className="absolute right-0 top-20 w-1/3 opacity-10">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path d="M50,10 L150,10 L150,110 L50,110 Z" fill="none" stroke="rgba(99, 102, 241, 0.8)" strokeWidth="0.5" />
            <path d="M60,20 L140,20 L140,100 L60,100 Z" fill="none" stroke="rgba(99, 102, 241, 0.8)" strokeWidth="0.5" />
            <circle cx="100" cy="60" r="20" fill="none" stroke="rgba(99, 102, 241, 0.8)" strokeWidth="0.5" />
          </svg>
        </div>
        
        <div className="absolute left-0 bottom-20 w-1/3 opacity-10">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path d="M10,50 L10,150 L110,150 L110,50 Z" fill="none" stroke="rgba(59, 130, 246, 0.8)" strokeWidth="0.5" />
            <circle cx="60" cy="100" r="20" fill="none" stroke="rgba(59, 130, 246, 0.8)" strokeWidth="0.5" />
          </svg>
        </div>
      </div>
      
      <Container>
        <div className="mx-auto max-w-4xl text-center mb-16 fade-in-up visible">
          <div className="inline-flex items-center justify-center gap-2 mb-3">
            <div className="h-px w-8 bg-blue-600/30"></div>
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest">Support & Resources</p>
            <div className="h-px w-8 bg-blue-600/30"></div>
          </div>
          
          <h2
            id="faqs-title"
            className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4"
          >
            Frequently Asked <span className="gradient-text font-extrabold">Questions</span>
          </h2>
          
          <p className="mt-4 text-lg md:text-xl leading-relaxed text-gray-600 max-w-2xl mx-auto">
            Find quick answers to common questions about AUDIT-AI. 
            For specific inquiries, our support team is ready to assist you.
          </p>
        </div>
        
        {/* Premium FAQ layout */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 stagger-fade-in visible no-js">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className={`transition-all duration-300 ${
                index !== faqs.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <button
                className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-blue-50/30 transition-colors duration-200"
                onClick={() => toggleFaq(index)}
                aria-expanded={activeIndex === index}
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">
                  {faq.question}
                </h3>
                <div className={`flex-shrink-0 rounded-full p-2 bg-blue-50 text-blue-600 transition-transform duration-300 ${activeIndex === index ? 'rotate-180 bg-blue-100' : ''}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ease-in-out bg-blue-50/20 ${
                  activeIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-8 py-6 text-base text-gray-700 leading-relaxed border-t border-gray-100">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Premium CTA section */}
        <div className="mt-20 max-w-4xl mx-auto rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 p-2 fade-in-up visible">
          <div className="bg-white rounded-xl p-8 sm:p-10 text-center flex flex-col items-center">
            <div className="inline-flex justify-center items-center p-3 mb-4 rounded-full bg-blue-50 text-blue-600">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Need more information?</h3>
            <p className="text-gray-600 max-w-2xl mb-8">
              Our core team is ready to answer your specific questions and provide a personalized demonstration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={handleContactClick}
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-blue-200 text-blue-600 font-medium hover:bg-blue-50 transition-colors duration-200 no-underline"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Support
              </button>
              <a 
                href="#get-started" 
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 no-underline"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/register');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Schedule a Demo
              </a>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
} 